import { useMemo } from 'react'
import { RewardsGQL } from '../gql'
import { RewardsFilter } from '../types'
import { PointsLedgerItemQueryResult } from '../gql/getPointsLedger.query'
import { formatToMonthYear } from '@/src/common/utils/date'

const getSection = (item: PointsLedgerItemQueryResult) => formatToMonthYear(new Date(item.date))

export const usePointsLedgerView = (view: RewardsFilter) => {
  const { ledger } = RewardsGQL.usePointsLedger()

  const filteredLedger = useMemo(() => {
    const settledLedger = ledger.filter((item) => !item.isPending)
    switch (view) {
      case 'All':
        return settledLedger
      case 'Card':
        return settledLedger.filter(
          (item) =>
            item.__typename === 'CardPurchasePointsLedgerItem' ||
            item.__typename === 'CardReturnPointsLedgerItem',
        )
      case 'Earned':
        return settledLedger.filter((item) => !item.points.isNegative)
      case 'Spent':
        return settledLedger.filter(
          (item) =>
            item.__typename === 'ExternalRedemptionPointsLedgerItem' ||
            item.__typename === 'StatementCreditRedemptionPointsLedgerItem' ||
            item.__typename === 'UnknownSpendPointsLedgerItem' ||
            item.__typename === 'MortgageTransferPointsLedgerItem',
        )
      case 'Mortgage':
        return settledLedger.filter(
          (item) =>
            item.__typename === 'MortgageSpendPointsLedgerItem' ||
            item.__typename === 'MortgageLoanPointsLedgerItem' ||
            item.__typename === 'MortgageTransferPointsLedgerItem',
        )
      default:
        view satisfies never
        throw new Error(`Unknown view: ${view}`)
    }
  }, [ledger, view])

  const sections = useMemo(
    () =>
      filteredLedger.reduce<{ title: string; data: PointsLedgerItemQueryResult[] }[]>(
        (acc, item) => {
          const section = getSection(item)
          const existingSection = acc.find((s) => s.title === section)
          if (!existingSection) {
            acc.push({ title: section, data: [item] })
          } else {
            existingSection.data.push(item)
          }
          return acc
        },
        [],
      ),
    [filteredLedger],
  )

  return { sections }
}
